import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ContextmenuType, SortDirection } from '@swimlane/ngx-datatable';
import { TableColumn as NgxTableColumn } from '@swimlane/ngx-datatable';
import { NgxSpinnerService } from 'ngx-spinner';

import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  NEVER,
  Observable,
  of,
  Subject,
  throwError,
} from 'rxjs';
import {
  catchError,
  delay,
  filter,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { Nullable } from '@src/app/core/models';

import {
  EMPTY_TABLE_SOURCE,
  getSimpleColumnSettings,
  isContextMenuOnBody,
  Page,
  projectToMap,
  TableColumn,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableInputs,
  TablePageEvent,
  TableRow,
  TableSelection,
  TableSortType,
  TemplateField,
  transformColumnSetting,
  transformFields,
} from './table';
import { TemplateCellIdDirective } from './template-cell.directive';

const MapSortType = {
  asc: 'asc' as 'asc',
  desc: 'desc' as 'desc',
};

/**
 * # How to use ui-table compoenent
 *
 * You can use only one ui-table in a parent component.
 * This is done to avoid to much stuff in only one place.
 *
 * Inside the template:
 *
 * ```typescript
 *  <ui-table
 *    [fields]="fields"
 *    [pager]="pager$"
 *    [rowSelection]="'checkbox'"
 *    [reload]="reload$"
 *    [contextMenuActions]="menusContextMenus"
 *    (select)="selectMenusHandler($event)"
 *    (rowAction)="editRowHandler($event)>
 * </ui-table>
 * ```
 *
 * Inside the TS file:
 * ```
 * @Component({
 *  selector: 'app-custom-component',
 *  templateUrl: './custom-component.component.html',
 *  styleUrls: ['./custom-component.component.scss'],
 * })
 * export class CustomComponent implements UiTableComponent<Menu> {
 *
 *   selectedMenus: CustomModel[] = [];
 *
 *   readonly fields: Array<TableField<CustomModel>> = [
 *     {
 *       selectable: true,
 *       header: '',
 *       key: 'chekbox',
 *     },
 *     {
 *       key: 'name',
 *       header: this.translate.instant('MENUS.TABLE.FIELDS.NAME'),
 *       sortable: true,
 *     },
 *     {
 *       key: 'canteenName',
 *       header: this.translate.instant('MENUS.TABLE.FIELDS.CANTEEN'),
 *       template: 'async',
 *     },
 *     {
 *       key: 'floor',
 *       header: this.translate.instant('MENUS.TABLE.FIELDS.FLOOR'),
 *     },
 *     {
 *       key: 'type',
 *       header: this.translate.instant('MENUS.TABLE.FIELDS.TYPE'),
 *       context: (x) => x.type ? getMenuTypeLabel(x.type) : '-',
 *     },
 *     {
 *       key: 'dateEnd',
 *       header: this.translate.instant('MENUS.TABLE.FIELDS.DATE_END'),
 *       template: 'date',
 *     },
 *     {
 *       key: 'dateEntered',
 *       header: this.translate.instant('MENUS.TABLE.FIELDS.CREATED_AT'),
 *       template: 'date-time',
 *     },
 *     {
 *       key: 'edit',
 *       header: '',
 *       template: 'edit',
 *       context: (x) => x,
 *     },
 *   ];
 *
 * readonly menusContextMenus: Array<TableContextMenu<CustomModel>> = [
 *   {
 *     label: this.translate.instant('MENUS.TABLE.ACTIONS.EDIT'),
 *     action: (menu) => this.router.navigate(['/menus/menu', menu.id]),
 *   },
 *   {
 *     label: this.translate.instant('MENUS.TABLE.ACTIONS.DELETE'),
 *     action: (menu) => this.deleteMenu(menu),
 *   },
 * ];
 * readonly reload$ = new BehaviorSubject(true);
 * readonly pager$ = ((page?: number, itemsPage?: number) => this.menuApi.getMenus(undefined, itemsPage, page).pipe(
 *   map((menuPage) => {
 *     const items = menuPage.items.map((item) => ({
 *       ...item,
 *       canteenName: item.canteenId
 *         ? this.canteenApi.getCanteenById(item.canteenId).pipe(map((canteen) => canteen.name))
 *         : of('-'),
 *     }));
 *     return { ...menuPage, items };
 *   }),
 * ));
 * ```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  // tslint:disable-next-line:component-selector
  selector: 'ui-table',
  styleUrls: ['./table.component.scss'],
  templateUrl: './table.component.html',
})
export class TableComponent<T> implements AfterViewInit, OnDestroy, OnInit {

  readonly ContextmenuType = ContextmenuType;

  itemsCount = 0;

  isLoading = false;
  isLoadingDataError = false;

  source$: Observable<TableInputs<T>> = of(EMPTY_TABLE_SOURCE);
  fields$: Observable<any[]> = of(EMPTY_TABLE_SOURCE.fields); // TODO: Improve typeing

  @Input() messages = {
    emptyMessage: this.translate.instant('Nessun dato disponibile'),
    selectedMessage: this.translate.instant('Selezionato'),
    totalMessage: this.translate.instant('Totale'),
  };

  @Input() classes = '';
  @Input() overrideDefaultStyle = false;

  @Input() rowSelection: 'checkbox' | 'single' | undefined = undefined;

  @Input() fields?: Array<TableField<T>>;
  @Input() columnSettings?: Array<TableColumn<T>>;
  @Input() contextMenuActions: Array<TableContextMenu<T>> = [];
  @Input() externalPaging = true;

  @Input() reload = new BehaviorSubject(true);

  @Input() pager: (page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
    Observable<Page<T> & { data: T[] }> = (() => NEVER);

  private readonly itemsPerPage$ = new BehaviorSubject(100);

  @Input() set itemsPerPage(value: number) {
    this.itemsPerPage$.next(value);
  }

  get itemsPerPage() {
    return this.itemsPerPage$.value;
  }

  @Input() selected: T[] = [];

  private readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);

  @Input() set filters(value: TableFilterType | undefined) {
    this.isLoading = true;

    this.filters$.next(value);
    this.itemsPage$.next(0);
  }

  get filters() {
    return this.filters$.value;
  }

  private readonly itemsPage$ = new BehaviorSubject<number>(0);

  @Input() set itemsPage(value: number) {
    this.itemsPage$.next(value);
  }

  get itemsPage() {
    return this.itemsPage$.value;
  }

  private readonly itemsSort$ = new BehaviorSubject<TableSortType>([]);

  get itemsSort() {
    return this.itemsSort$.value;
  }

  set itemsSort(value: TableSortType) {
    this.itemsSort$.next(value);
  }

  @Input() set defaultSort(value: TableSortType) {
    if (value && value.length > 0) {
      this.itemsSort$.next(value);
    }
  }

  get sorts(): Array<{ dir: SortDirection, prop: string }> {
    return this.itemsSort.map((i) => ({ dir: SortDirection[i.type], prop: i.field }));
  }

  get allClasses() {
    if (this.overrideDefaultStyle) {
      return this.classes;
    }
    return `${this.classes} ui-table`;
  }

  @Input() pagination = true;
  @Input() virtualScroll = false;


  // tslint:disable-next-line:no-output-native
  @Output() readonly select = new EventEmitter<TableSelection<T>>();
  @Output() readonly itemsPageChange = new EventEmitter<TablePageEvent>();
  @Output() readonly rowAction = new EventEmitter<{ type: TemplateField; item: T }>();

  @ViewChildren(TemplateCellIdDirective)
  private readonly cellTemplateRefs?: QueryList<TemplateCellIdDirective>;

  @ContentChildren(TemplateCellIdDirective)
  private readonly cellExternalTemplateRefs?: QueryList<TemplateCellIdDirective>;

  private readonly templates$ = new BehaviorSubject<Record<string, TemplateRef<any>>>({});

  @ViewChild('tableContextMenu', { static: true })
  private readonly tableContextMenu?: TemplateRef<any>;

  private overlayRef?: Nullable<OverlayRef>;

  private readonly destroy$ = new Subject();

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly spinnerService: NgxSpinnerService,
    private readonly translate: TranslateService,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
  }

  ngOnInit() {
    this.source$ = combineLatest([
      this.itemsPage$,
      this.itemsPerPage$,
      this.itemsSort$,
      this.reload,
    ]).pipe(
      tap(() => {
        this.isLoading = true;
        this.isLoadingDataError = false;

        this.spinnerService.show('loadingTable');
      }),
      switchMap(([page, itemsPerPage, sort]) => {
        // +1 is done beacuse ngx-datatable start to 0 with page offset
        return this.pager(page + 1, itemsPerPage, sort, this.filters);
      }),
      map((page) => {
        const source: TableInputs<T> = {
          data: page.data,
          fields: [], // TODO: Remove field
          settings: this.columnSettings // TODO: Remove field because unused
            ? transformColumnSetting(this.columnSettings)
            : getSimpleColumnSettings(this.fields || []),
        };

        this.itemsCount = page.count || page.data.length;
        this.isLoading = false;
        return source;
      }),
      delay(500),
      finalize(() => {
        this.spinnerService.hide('loadingTable');
      }),
      catchError((error) => {
        this.isLoading = false;
        this.isLoadingDataError = true;

        return throwError(() => new Error(error));
      }),
    );

    this.fields$ = combineLatest([
      this.breakpointObserver.observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
      ]),
      this.templates$,
    ]).pipe(
      map(([breakpoint, templates]) => {
        const originalFields = this.fields || [];
        const visibleFields = originalFields
          .filter((f) => {
            const fieldBreakpoints = f.hideBreakpoints || [];
            const isHidden = fieldBreakpoints.some((fb) => breakpoint.breakpoints[fb]);

            return !isHidden;
          })
          .filter((f) => {
            const fieldBreakpoints = f.showBreakpoints || [];
            const isVisible = fieldBreakpoints.some((fb) => breakpoint.breakpoints[fb]);

            return f.showBreakpoints ? isVisible : true;
          });
        return transformFields(visibleFields || [], templates);
      }),
    );

  }

  ngOnDestroy() {
    this.closeContextMenu();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    if (this.cellTemplateRefs && this.cellExternalTemplateRefs) {
      const templates = this.cellTemplateRefs.toArray();
      const externalTemplates = this.cellExternalTemplateRefs.toArray();

      const allTemplates = [...templates, ...externalTemplates];
      this.templates$.next(projectToMap(allTemplates, 'id', (x) => x.templateRef));
    }
  }

  onCellClick(event: { type: string, column: any, row: T, cellElement: HTMLElement }) {
    // Only handle single clicks on cells, not double-clicks or other activation events
    if (event.type === 'click' && event.column) {
      const clickableField = this.fields?.find(f => f.key === event.column.prop && f.click);
      if (clickableField?.click) {
        clickableField.click(event.row as TableRow<T>);
      }
    }
  }

  changePageHandler(page: TablePageEvent) {
    if (this.externalPaging) {
      this.itemsPageChange.emit(page);
    } else {
      this.itemsPage = page.pageIndex;
      this.itemsPerPage = page.pageSize;
    }
  }

  onSortHandler(data: { sorts: Array<{ dir: SortDirection, prop: string }> }) {
    const sort = data.sorts
      .reverse()
      .map((s) => ({ field: s.prop, type: MapSortType[s.dir] }));

    this.itemsSort = sort;
  }

  onSelectHandler(selection: TableSelection<T> | Event | any) {
    if (!selection || selection instanceof Event || !Array.isArray(selection.selected)) {
      return;
    }

    this.selected = selection.selected;
    this.select.emit(selection);
  }

  contextMenuHandler(data: { event: MouseEvent, type: ContextmenuType, content: NgxTableColumn | T }) {
    if (!this.tableContextMenu) {
      throw new Error('Cannot open context menu without a TemplateRef');
    }

    if (isContextMenuOnBody(data.type, data.content)) {
      this.closeContextMenu();

      const positionStrategy = this.overlay.position()
        .flexibleConnectedTo({ x: data.event.x, y: data.event.y })
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.close(),
      });

      this.overlayRef.attach(new TemplatePortal(this.tableContextMenu, this.viewContainerRef, {
        $implicit: data.content,
      }));

      fromEvent<MouseEvent>(document, 'click')
        .pipe(
          filter(() => !!this.overlayRef),
          take(1),
        ).subscribe(() => this.closeContextMenu());
    }

    data.event.preventDefault();
    data.event.stopPropagation();
  }

  private closeContextMenu() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
